import React, { memo, forwardRef, useImperativeHandle, useCallback } from 'react'
import PropTypes from 'prop-types'
import MuiDrawer from '@material-ui/core/Drawer'

/**
 * Implements the material UI drawer component.
 *
 * @component
 * @param {object} props component props
 * @returns {GeneralDrawer} GeneralDrawer component
 */
const GeneralDrawer = memo(forwardRef((props, ref) => {
  const { drawerContent, defaultOpen, ...finalProps } = props
  const [open, setOpen] = React.useState(props.defaultOpen)

  const handleDrawerOpen = useCallback(() => {
    setOpen(true)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setOpen(false)
  }, [])

  useImperativeHandle(ref, () => ({
    open: () => {
      handleDrawerOpen()
    }
  }))

  return (
    <MuiDrawer
      {...finalProps}
      variant={props.variant}
      anchor={props.anchor}
      open={open}
      onClose={handleDrawerClose}
    >
      <props.drawerContent handleDrawerClose={handleDrawerClose} />
    </MuiDrawer>
  )
}))

GeneralDrawer.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   * Used to open the sidebar by default
   */
  defaultOpen: PropTypes.bool,
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['permanent', 'persistent', 'temporary']).isRequired,
  /**
   * Side from which the drawer will appear.
   */
  anchor: PropTypes.oneOf(['left', 'right']).isRequired,
  /**
   * Drawer content
   */
  drawerContent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func
  ]),
}

GeneralDrawer.defaultProps = {
  defaultOpen: false
}

export default GeneralDrawer
